import { Alert, BadgeIcon, RadioGroup, Tooltip } from '@economic/taco';
import { Trans, t } from '@lingui/macro';

import ExternalLink from '~/app/shared/components/ExternalLink';
import { RackbeatPrinciple } from './types';

interface Props {
    children: string | JSX.Element;
    inventoryPrinciple: RackbeatPrinciple;
    setInventoryPrinciple: (inventoryPrinciple: RackbeatPrinciple) => void;
    trackEvent: (event: string) => void;
}

export default function InventoryPrinciple({ inventoryPrinciple, setInventoryPrinciple, trackEvent, children }: Props) {
    return (
        <>
            <div className="mb-2">{children}</div>
            <div className="text-center mt-4 ">
                <h2 className="mb-6">
                    <Trans>Choose inventory principle</Trans>
                </h2>
            </div>
            <div className="mb-6">
                <Alert state="information" title="">
                    <Trans>
                        Your choice of inventory principle is important for how you manage your inventory.{' '}
                        <ExternalLink
                            href="https://www.e-conomic.dk/support/artikler/vaelg-lagerprincip-i-lager-af-rackbeat"
                            onClick={() => trackEvent('principle-learn-more-link.click')}>
                            Read more about inventory principles
                        </ExternalLink>
                    </Trans>
                </Alert>
            </div>
            <div className="mt-6">
                <div className="font-bold mb-1">
                    <Trans>Which inventory principle will you use?</Trans>
                </div>
                <RadioGroup
                    name="inventoryPrinciple"
                    value={inventoryPrinciple}
                    onChange={value => {
                        const selectedPrinciple = value as RackbeatPrinciple;
                        setInventoryPrinciple(selectedPrinciple);
                        trackEvent(`${selectedPrinciple === RackbeatPrinciple.Average ? 'average' : 'fifo'}.selected`);
                    }}>
                    <RadioGroup.Item value={RackbeatPrinciple.Average}>
                        <Trans>Average cost price</Trans>
                        <Tooltip title={t`Calculates the costs of the items based on an average of all purchases`}>
                            <BadgeIcon icon="info" color="blue" small subtle />
                        </Tooltip>
                    </RadioGroup.Item>
                    <RadioGroup.Item value={RackbeatPrinciple.Fifo}>
                        <Trans>FIFO (First In, First Out)</Trans>
                        <Tooltip title={t`The items that enter the warehouse first are also the first to be sold or used`}>
                            <BadgeIcon icon="info" color="blue" small subtle />
                        </Tooltip>
                    </RadioGroup.Item>
                </RadioGroup>
            </div>
            <div className="mt-6 mb-6">
                <Trans>
                    <strong>OBS!</strong> You can only change your inventory principle before performing the first inventory
                    action in the system. After that, the principle is locked.
                </Trans>
            </div>
        </>
    );
}
